import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { rvApi } from '../api';
import { ROUTES } from '../../../constants/routes';

function RulesViolationInspection() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [itemForm, setItemForm] = useState({
    comments: '',
    witnessInformation: '',
    violationNumber: '',
    dueDateCorrection: '',
  });
  const [itemFiles, setItemFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [insp, grp] = await Promise.all([
        rvApi.getInspection(id),
        rvApi.groups(),
      ]);
      setData(insp.data);
      setGroups(grp.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load violation report');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!groupId) {
      setTypes([]);
      setTypeId('');
      return;
    }
    rvApi.types(groupId).then((res) => setTypes(res.data)).catch(() => setTypes([]));
    setTypeId('');
  }, [groupId]);

  useEffect(() => {
    if (!data?.property_id || data.status === 'submitted') return;
    rvApi.previewNumber(data.property_id)
      .then((res) => setItemForm((f) => ({ ...f, violationNumber: res.data.violationNumber })))
      .catch(() => {});
  }, [data?.property_id, data?.status]);

  const staff = data?.staff || {};
  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  const submitViolation = async () => {
    setBusy(true);
    setError('');
    try {
      await rvApi.submitInspection(id, { outcome: 'has_violations' });
      navigate(ROUTES.RULES_VIOLATION);
    } catch (e) {
      setError(e.response?.data?.error || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!typeId) {
      setError('Select a violation type.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await rvApi.addItem(id, {
        violationTypeId: Number(typeId),
        comments: itemForm.comments,
        witnessInformation: itemForm.witnessInformation,
        violationNumber: itemForm.violationNumber,
        dueDateCorrection: itemForm.dueDateCorrection || null,
      });
      for (const file of itemFiles) {
        await rvApi.uploadItemPhoto(id, res.data.id, file);
      }
      setItemFiles([]);
      setItemForm({ comments: '', witnessInformation: '', violationNumber: '', dueDateCorrection: '' });
      setTypeId('');
      await load();
      if (data?.property_id) {
        const num = await rvApi.previewNumber(data.property_id);
        setItemForm((f) => ({ ...f, violationNumber: num.data.violationNumber }));
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to add violation');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;
  if (!data) return <p className="text-danger">{error || 'Not found'}</p>;

  if (data.status === 'submitted') {
    return (
      <div className="alert alert-success">
        Violation report submitted.{' '}
        <button type="button" className="btn btn-sm btn-outline-primary ms-2" onClick={() => navigate(ROUTES.RULES_VIOLATION)}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card mb-3">
        <div className="card-header">Violation details</div>
        <div className="card-body small">
          <div className="row">
            <div className="col-md-6">
              <p><strong>Community:</strong> {data.property_name}</p>
              <p><strong>Site:</strong> {data.location_name || '—'}</p>
              <p><strong>Site address:</strong> {data.location_address || '—'}</p>
              <p><strong>Resident/guest:</strong> {data.resident_name || '—'}</p>
              <p><strong>Date reported:</strong> {fmt(data.inspected_at)}</p>
            </div>
            <div className="col-md-6">
              <p><strong>Community manager:</strong> {staff.community_manager_name || '—'}</p>
              <p><strong>Manager email:</strong> {staff.community_manager_email || '—'}</p>
              <p><strong>Assistant manager:</strong> {staff.assistant_manager_name || '—'}</p>
              <p><strong>Assistant email:</strong> {staff.assistant_manager_email || '—'}</p>
              <p><strong>RVP:</strong> {staff.rvp_name || '—'}</p>
              <p><strong>RVP email:</strong> {staff.rvp_email || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {data.items?.length > 0 && (
        <div className="card mb-3">
          <div className="card-header">Recorded violations ({data.items.length})</div>
          <ul className="list-group list-group-flush small">
            {data.items.map((it) => (
              <li key={it.id} className="list-group-item">
                <strong>{it.group_name}</strong> — {it.violation_type_name} (#{it.violation_number})
                {it.due_date_correction && ` — due ${it.due_date_correction}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      <form className="card mb-3" onSubmit={addItem}>
        <div className="card-header">Add violation</div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Group</label>
              <select className="form-select" value={groupId} onChange={(e) => setGroupId(e.target.value)} required>
                <option value="">Select group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Violation</label>
              <select className="form-select" value={typeId} onChange={(e) => setTypeId(e.target.value)} required disabled={!groupId}>
                <option value="">Select violation…</option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Violation number</label>
              <input className="form-control" value={itemForm.violationNumber} onChange={(e) => setItemForm({ ...itemForm, violationNumber: e.target.value })} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Due date for correction</label>
              <input type="date" className="form-control" value={itemForm.dueDateCorrection} onChange={(e) => setItemForm({ ...itemForm, dueDateCorrection: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label">Comments</label>
              <textarea className="form-control" rows={2} value={itemForm.comments} onChange={(e) => setItemForm({ ...itemForm, comments: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label">Witness information</label>
              <textarea className="form-control" rows={2} value={itemForm.witnessInformation} onChange={(e) => setItemForm({ ...itemForm, witnessInformation: e.target.value })} />
            </div>
            <div className="col-12">
              <label className="form-label">Photos</label>
              <input type="file" accept="image/*" multiple className="form-control" onChange={(e) => setItemFiles(Array.from(e.target.files || []))} />
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-3">
            <button type="submit" className="btn btn-primary" disabled={busy}>Add violation</button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(ROUTES.RULES_VIOLATION)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-success ms-auto"
              disabled={busy || !data.items?.length}
              onClick={submitViolation}
            >
              Submit violation
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default RulesViolationInspection;
